import { useSetRecoilState } from 'recoil';
import {
  PlanPickerBody,
  PlanPickerResponse,
  TargetPlatform,
  ApiPaginatedResponseBodyOfPlanPickerResponse
} from '../../../../API';
import {
  CacheManager,
  DataManagerHelpers,
  DataManagerHook,
  DataManagerHookReturnType,
  IDataManagerHookProps
} from '../../../DataManager';

export interface PlanPickerHookReturnType extends DataManagerHookReturnType<PlanPickerResponse, PlanPickerBody> {
  getByCountry: (country: string) => Promise<PlanPickerResponse[] | null>;
  fetchPaginatedWithFilters: (
    limit?: number,
    page?: number,
    country?: string,
    platforms?: TargetPlatform[],
    includeDeleted?: boolean
  ) => Promise<ApiPaginatedResponseBodyOfPlanPickerResponse | null>;
}

export function PlanPickerHook(
  params: IDataManagerHookProps<PlanPickerResponse, PlanPickerBody>
): PlanPickerHookReturnType {
  const dataManagerHook = DataManagerHook<PlanPickerResponse, PlanPickerBody>(params);
  const api = params.useApiHook() as ReturnType<
    typeof import('../../../../hooks/API/PlanPicker/usePlanPickerApi').usePlanPickerApi
  >;
  const setCache = useSetRecoilState(params.state.withDataCache);
  const { addRecordToCache } = CacheManager(params.idField, setCache);
  const { handleApiError } = DataManagerHelpers({ name: params.name, toString: params.toString });

  const getByCountry = async (country: string): Promise<PlanPickerResponse[] | null> => {
    try {
      const response = await api.getByCountry(country);
      const planPickers = response.data.body;
      if (planPickers) {
        planPickers.forEach((planPicker: PlanPickerResponse) => addRecordToCache(planPicker));
        return planPickers;
      }
      return null;
    } catch (err) {
      handleApiError(err, 'get_by_country');
      return null;
    }
  };

  const fetchPaginatedWithFilters = async (
    limit = 50,
    page = 1,
    country?: string,
    platforms?: TargetPlatform[],
    includeDeleted?: boolean
  ): Promise<ApiPaginatedResponseBodyOfPlanPickerResponse | null> => {
    try {
      const response = await api.getPaginated(limit, page, country, platforms, includeDeleted);
      const paginatedResponse = response.data.body;
      if (paginatedResponse?.results) {
        paginatedResponse.results.forEach((planPicker: PlanPickerResponse) => addRecordToCache(planPicker));
      }
      return paginatedResponse || null;
    } catch (err) {
      handleApiError(err, 'fetch_paginated_with_filters');
      return null;
    }
  };

  return {
    ...dataManagerHook,
    getByCountry,
    fetchPaginatedWithFilters
  };
}
